import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Card } from '@/components/ui/Card';
import { useWaterSlotRules } from '@/hooks/useWaterSlotRules';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import type { FarmResponse } from '@/types/api';

const AFTERNOON = '3pm';
const SALINITY = 'salinity_ppt';

/**
 * Which field tests a farm takes at which slot (#371). Only the common case is a control here:
 * salinity measured in the morning only, so the afternoon no longer lists it as missing. The
 * underlying setting (water_fields_not_measured) is a general slot -> columns map; other entries
 * are kept as they are.
 */
export function WaterFieldsEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const off = useWaterSlotRules(farmId) ?? {};
  const morningOnly = !!off[AFTERNOON]?.includes(SALINITY);

  const save = useMutation({
    mutationFn: async (next: boolean) => {
      // The save replaces the whole map: start from the server's current one, not the cached copy.
      const fresh = await queryClient.fetchQuery({
        queryKey: ['farm', farmId],
        queryFn: () => api.get<{ data: FarmResponse }>(`/api/v1/farms/${farmId}`),
        staleTime: 0,
      });
      const current: Record<string, string[]> = { ...(fresh.data?.settings?.water_fields_not_measured ?? {}) };
      const afternoon = (current[AFTERNOON] ?? []).filter((c) => c !== SALINITY);
      if (next) afternoon.push(SALINITY);
      const map: Record<string, string[]> = { ...current, [AFTERNOON]: afternoon };
      if (afternoon.length === 0) delete map[AFTERNOON];
      return api.put(`/api/v1/farms/${farmId}/settings`, { water_fields_not_measured: map });
    },
    onSuccess: () => {
      toast.success('Water readings setting saved');
      queryClient.invalidateQueries({ queryKey: ['farm', farmId] });
      queryClient.invalidateQueries({ queryKey: ['entry-ponds', farmId] });
    },
    onError: (err) => toast.error(`Not saved: ${saveFailureMessage(err, 'try again')}`),
  });

  return (
    <Card>
      <Card.Header>
        <Card.Title>Water readings</Card.Title>
      </Card.Header>
      <Card.Body>
        <label className="flex min-h-[44px] items-start gap-2 text-sm text-gray-900">
          <input
            type="checkbox"
            checked={morningOnly}
            disabled={!canEdit || save.isPending}
            onChange={(e) => save.mutate(e.target.checked)}
            className="mt-0.5 h-5 w-5"
            data-salinity-am-only
          />
          <span>
            <span className="font-medium">Salinity is measured in the morning only</span>
            <span className="block text-gray-500">
              The 3pm round then does not ask for it, and Today stops showing it as missing in the afternoon. Water colour
              stays available at both times.
              {!canEdit && ' Only a Farm Owner can change this.'}
            </span>
          </span>
        </label>
      </Card.Body>
    </Card>
  );
}
